import {
  forwardRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

import { cn } from '../../../lib/utils.js';

export interface CommandPaletteBackdropProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CommandPaletteBackdrop({
  className,
  children,
  ...props
}: CommandPaletteBackdropProps) {
  return (
    <div className={cn('cmdk-backdrop', className)} role="presentation" {...props}>
      {children}
    </div>
  );
}

export interface CommandPaletteShellProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CommandPaletteShell({
  className,
  children,
  role = 'dialog',
  ...props
}: CommandPaletteShellProps) {
  return (
    <div className={cn('cmdk', className)} role={role} aria-modal="true" {...props}>
      {children}
    </div>
  );
}

export interface CommandPaletteInputProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CommandPaletteInput({ className, children, ...props }: CommandPaletteInputProps) {
  return (
    <div className={cn('cmdk-input', className)} {...props}>
      {children}
    </div>
  );
}

export type CommandPaletteSearchInputProps = InputHTMLAttributes<HTMLInputElement>;

export const CommandPaletteSearchInput = forwardRef<
  HTMLInputElement,
  CommandPaletteSearchInputProps
>(function CommandPaletteSearchInput(props, ref) {
  return <input ref={ref} {...props} />;
});

export interface CommandPaletteListProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CommandPaletteList({ className, children, ...props }: CommandPaletteListProps) {
  return (
    <div className={cn('cmdk-list', className)} role="listbox" {...props}>
      {children}
    </div>
  );
}

export interface CommandPaletteSectionProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CommandPaletteSection({
  className,
  children,
  ...props
}: CommandPaletteSectionProps) {
  return (
    <div className={cn('cmdk-section', className)} {...props}>
      {children}
    </div>
  );
}

export interface CommandPaletteItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  children: ReactNode;
}

export function CommandPaletteItem({
  active = false,
  className,
  type = 'button',
  children,
  ...props
}: CommandPaletteItemProps) {
  return (
    <button type={type} className={cn('cmdk-item', active && 'is-active', className)} {...props}>
      {children}
    </button>
  );
}

export interface CommandPaletteItemIconProps {
  children: ReactNode;
  className?: string;
}

export function CommandPaletteItemIcon({ children, className }: CommandPaletteItemIconProps) {
  return <span className={cn('ci-icon', className)}>{children}</span>;
}

export interface CommandPaletteItemMetaProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
}

export function CommandPaletteItemMeta({
  className,
  children,
  ...props
}: CommandPaletteItemMetaProps) {
  return (
    <span className={cn('ci-meta', className)} {...props}>
      {children}
    </span>
  );
}

export interface CommandPaletteFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CommandPaletteFooter({ className, children, ...props }: CommandPaletteFooterProps) {
  return (
    <div className={cn('cmdk-footer', className)} {...props}>
      {children}
    </div>
  );
}
